import React, { FC, useEffect, useId, useState } from "react";
import type { TableColumnsType } from "antd";
import TableComponent from "../../atoms/TableComponent/Tablecomponent";
import { useAppDispatch, useAppSelector, } from "../../../app/utils/hooks/reduxHook";
import { getImageURL } from "../../../app/utils/common/getImageUrl";
import { image } from "../../../app/utils/common/image";
import { deleteUserAPI, getAllUserManagementAPI } from "../../../redux/API/AllUsersAPI";
import { setCurrentUser, setUserManagementLoading, setUsersManagementPagination } from "../../../redux/Slices/allUsersSlice";
import { AllUsersType } from "../../../dto/AllUsers.dto";
import ActionIcon from "../../atoms/ActionIcon/ActionIcon";
import OptionMenu from "../../atoms/OptionMenu/OptionMenu";
import ConfirmModal from "../../atoms/confirmation-modal/confirmation-modal";
import { confirmModalProps } from "../../../dto/confirm";
import { checkPermission, permissionType } from "../../../app/utils/common/checkPermission";
import { useNavigate } from "react-router-dom";


const ActionButtonContainer: React.FC<{ User: any, deleteUserFunction: (id: number) => void }> = ({ User, deleteUserFunction }) => {
    const [isMenuOpen, seIsMenuOpen] = useState(false);
    const [removeModal, setRemoveModal] = useState<boolean>(false);
    // const { loading } = useAppSelector(state => state.manager)
    const { currentUser, loading } = useAppSelector(state => state.allUsers)

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const options = [
        checkPermission(permissionType.USER_MODULE, "update") &&
        {
            name: "Edit",
            onClick: () => {
                dispatch(setCurrentUser(User));
                navigate('user/edit')
                
            }
        },
        checkPermission(permissionType.USER_MODULE, "delete") &&
        {
            name: "Remove",
            onClick: () => {
                setRemoveModal(true);
            }
        }
    ].filter(Boolean) as { name: string; onClick: () => void }[];


    const confirmModalProps: confirmModalProps = {
        text: "Are you sure you want to remove this User?",
        confirmModal: removeModal,
        setConfirmModal: setRemoveModal,
        buttons: ['Yes, Remove', 'Cancel'],
        loading: loading,
        onPressPositive: () => { deleteUserFunction(User?.user_id); setRemoveModal(false); },
        onPressNegative: () => { setRemoveModal(false) },
        image: image.USER_NAME_ICON
    };

    return (
        <div className='action_tab' style={{ position: "relative" }}>
            <div className='action-group' onClick={() => seIsMenuOpen(!isMenuOpen)}>
                <ActionIcon />
            </div>
            {isMenuOpen && <OptionMenu options={options} left={"-100px"} top={'32px'} onOutSideClick={() => seIsMenuOpen(false)} />}
            {removeModal && <ConfirmModal confirmModalProps={confirmModalProps} />}

        </div>
    )
}

const UserManagementTable: FC = () => {
    const { userManagementPagination, loading, UserManagement } = useAppSelector((state) => state.allUsers);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!loading) {
            dispatch(getAllUserManagementAPI({ page: userManagementPagination.current_page, filterBy: "active" })).unwrap().then(() => {
                dispatch(setUserManagementLoading(false));
            })
        }
    }, [userManagementPagination.current_page]);


    const handleDeleteUser = (id: number) => {
        try {
            dispatch(setUserManagementLoading(true)); // Set loading state before deletion
            dispatch(deleteUserAPI({ user_id: id })).unwrap().then(() => {
                dispatch(getAllUserManagementAPI({ page: userManagementPagination.current_page, filterBy: "active" })).unwrap();
            })            
        } catch (err: any) {
            console.error("Error in deleting user:", err);
        } finally {
            dispatch(setUserManagementLoading(false)); // Reset loading state
        }
    };
    
    // Handle page changes
    const handlePageChange = (pageNumber: number) => {
        if (!loading) {
            let tempData = { ...userManagementPagination };
            tempData.current_page = pageNumber;
            dispatch(setUsersManagementPagination(tempData));
        }
    };


    const columns: TableColumnsType<AllUsersType> = [
        {
            title: "Profile",
            dataIndex: "profile_image",
            render: (profile) => (
                <img
                    src={getImageURL(profile) || image.DEFAULT_PROFILE_IMAGE}
                    onError={(e: any) => (e.target.src = image.DEFAULT_PROFILE_IMAGE)}
                    alt="Profile"
                    className="reg-profile"
                />
            ),
        },
        {
            title: "Name",
            dataIndex: "name",
        },
        {
            title: "Phone Number",
            dataIndex: "phone_number",
            render: (_, record) => (
                <div style={{ textAlign: "center" }}>
                    {record.country_code} {record.phone_number}
                </div>
            )
        },
        {
            title: "Role",
            dataIndex: "role",
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (text, record) => (
                <ActionButtonContainer User={record} deleteUserFunction={handleDeleteUser} />
            ),
        },
        
    ];

    return (
        <div className="manager_dataTable">
            <TableComponent
                columns={columns}
                loading={loading}
                dataSource={UserManagement}
                pageNumbers={Array.from({ length: userManagementPagination.total_pages }, (_, index) => index + 1)}
                handlePageLink={handlePageChange}
                isPagination={userManagementPagination.total_pages > 1}
                totalProfile={userManagementPagination.total_data}
                prevPage={() => handlePageChange(userManagementPagination.current_page - 1)}
                nextPage={() => handlePageChange(userManagementPagination.current_page + 1)}
                activePage={userManagementPagination.current_page}
                currentTotal={Math.min(userManagementPagination.current_page * userManagementPagination.page_size, UserManagement.length)}
            />
        </div>
    );
};

export default UserManagementTable;
